import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { projectApi } from "@/services/projectService";
import { ahjApi } from "@/services/ahjService";
import { IAHJDocument } from "@/dto/get-project-response.dto";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, CheckCircle, AlertCircle, Upload, X, FileText } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface AHJRequirementsUploadProps {
  projectId: string;
  ahjId: string;
  onComplete: () => void;
  onSkip: () => void;
  showContinueButton?: boolean;
}

const permittingRequirementsLabels: Record<string, string> = {
  slabUpliftTest: "Slab Uplift Test",
  slabCoreTest: "Slab Core Test",
  adaPlans: "ADA Plans",
  photometricPlans: "Photometric Plans",
  egressPlans: "Egress Plans",
  smokeVentInspections: "Smoke Vent Inspections",
  fpeStamp: "FPE Stamp",
  weightSigns: "Weight Signs",
  flueKeepersPalletStops: "Flue Keepers Pallet Stops",
  keepClearsStickers: "Keep Clears Stickers",
  fireSignsCommoditySigns: "Fire Signs/Commodity Signs",
};

export const AHJRequirementsUpload = ({
  projectId,
  ahjId,
  onComplete,
  onSkip,
  showContinueButton = true,
}: AHJRequirementsUploadProps) => {
  const { toast } = useToast();
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  // Fetch Project to get uploaded docs
  const { data: projectData, refetch: refetchProject } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projectApi.getProject(projectId),
  });

  // Fetch AHJ to get requirements
  const { data: ahjData } = useQuery({
    queryKey: ["ahj", ahjId],
    queryFn: () => ahjApi.getAHJ(ahjId),
  });

  const project = projectData;
  const ahj = ahjData?.ahj;

  const { mutate: uploadFile } = useMutation({
    mutationFn: ({ key, file }: { key: string; file: File }) =>
      projectApi.uploadAHJDocument(projectId, key, file),
    onMutate: ({ key }) => setUploadingKey(key),
    onSuccess: () => {
      toast({ title: "File uploaded successfully" });
      refetchProject();
      setUploadingKey(null);
    },
    onError: () => {
      toast({ title: "Upload failed", variant: "destructive" });
      setUploadingKey(null);
    },
  });

  const { mutate: deleteFile } = useMutation({
    mutationFn: (documentId: string) =>
      projectApi.deleteAHJDocument(projectId, documentId),
    onSuccess: () => {
      toast({ title: "File deleted" });
      refetchProject();
    },
  });

  if (!project || !ahj) {
    return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
  }

  const requiredKeys = Object.entries(ahj.permittingRequirements || {})
    .filter(([_, required]) => required)
    .map(([key]) => key);

  const uploadedDocs = project.ahjDocuments || [];
  
  const isRequirementMet = (key: string) => {
    return uploadedDocs.some((doc: IAHJDocument) => doc.requirementKey === key);
  };

  const getUploadedDoc = (key: string) => {
    return uploadedDocs.find((doc: IAHJDocument) => doc.requirementKey === key);
  };

  const allMet = requiredKeys.every(key => isRequirementMet(key));

  const handleFileChange = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadFile({ key, file: e.target.files[0] });
    }
  };

  return (
    <div className="space-y-4 w-full mx-auto py-2 sm:py-6">
      <div className="text-center space-y-2 px-2">
        <h2 className="text-xl sm:text-2xl font-bold">AHJ Requirements</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Please upload the required documents for {ahj.name}.
        </p>
      </div>

      <Card className="border-border/50 shadow-sophisticated">
        <CardHeader className="px-2 sm:px-6 py-4 border-b border-border/50">
          <CardTitle className="text-lg sm:text-xl">Required Documents</CardTitle>
          <CardDescription>
            Upload files for each requirement below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 px-2 sm:px-6 pt-6">
          {requiredKeys.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No specific requirements found for this AHJ.
            </div>
          ) : (
            requiredKeys.map((key) => {
              const doc = getUploadedDoc(key);
              const isUploading = uploadingKey === key;

              return (
                <div
                  key={key}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border border-border/50 rounded-lg bg-card/50 gap-3 sm:gap-4 hover:bg-accent/50 transition-colors shadow-sm"
                >
                  <div className="space-y-1 w-full">
                    <div className="flex items-center justify-between sm:justify-start gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm sm:text-base break-words text-foreground/90">
                          {permittingRequirementsLabels[key] || key}
                        </span>
                        {doc ? (
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        )}
                      </div>
                      {/* Show delete button next to title on mobile if doc exists to save space? 
                          No, let's keep it consistent. */}
                    </div>
                    {doc && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1 break-all bg-muted/50 p-1.5 rounded-md border border-border/50">
                        <FileText className="h-3 w-3 flex-shrink-0" />
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline truncate hover:text-primary transition-colors"
                        >
                          {doc.name}
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0 w-full sm:w-auto mt-1 sm:mt-0">
                    {doc ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteFile(doc._id)}
                        className="text-destructive hover:text-destructive ml-auto sm:ml-0 hover:bg-destructive/10"
                      >
                        <X className="h-4 w-4" />
                        <span className="sm:hidden ml-2">Remove</span>
                      </Button>
                    ) : (
                      <div className="relative w-full sm:w-auto">
                        <input
                          type="file"
                          id={`file-${key}`}
                          className="hidden"
                          onChange={(e) => handleFileChange(key, e)}
                          disabled={isUploading}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isUploading}
                          onClick={() =>
                            document.getElementById(`file-${key}`)?.click()
                          }
                          className="w-full sm:w-auto hover:bg-accent hover:text-accent-foreground transition-colors"
                        >
                          {isUploading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Upload
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse sm:flex-row justify-between pt-2 sm:pt-4 gap-3 px-2 sm:px-0">
        <Button variant="ghost" onClick={onSkip} className="w-full sm:w-auto hover:bg-accent hover:text-accent-foreground transition-colors">
          {showContinueButton ? "Skip for now" : "Close"}
        </Button>
        {showContinueButton && (
          <Button
            onClick={onComplete}
            disabled={!allMet && requiredKeys.length > 0}
            className="w-full sm:w-auto btn-gradient shadow-md hover:shadow-lg transition-all"
          >
            Continue to Submission
          </Button>
        )}
      </div>
    </div>
  );
};
